import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input
} from '@angular/core';
import { Sort } from '@angular/material/sort';
import { TableColumn, TableSort, TableSpec } from 'src/app/services/copilot/copilot.types';

type SortDirection = 'asc' | 'desc';

/** Seconds -> "Xh Ym" (fallback only — the gateway sends pre-formatted strings). */
function formatDurationSec(sec: number): string {
  const total = Math.max(0, Math.round(sec));
  const h = Math.floor(total / 3600);
  const m = Math.round((total % 3600) / 60);
  if (h <= 0) {
    return `${m}m`;
  }
  return `${h}h ${m}m`;
}

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function normalizeSummaryLabel(value: string): string {
  return value
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Gateway marks summary rows with isSummary; also accept any cell whose
 * visible text is "Total" (including extracted col_0 / markdown emphasis).
 */
export function isSummaryTableRow(row: Record<string, unknown>): boolean {
  if (row.isSummary === true || row.isSummary === 'true') {
    return true;
  }
  for (const [key, raw] of Object.entries(row)) {
    if (key === 'isSummary' || key.endsWith('Formatted')) {
      continue;
    }
    if (typeof raw !== 'string') {
      continue;
    }
    const label = normalizeSummaryLabel(raw);
    if (label === 'total' || label === 'totals' || label === 'grand total') {
      return true;
    }
  }
  return false;
}

const TEXT_COL_MIN_PX = 140;
const TEXT_COL_MIN_COMPACT_PX = 120;
const TEXT_COL_PAD_PX = 32;
/** ~14px UI font; keep columns aligned under flex mat-table. */
const TEXT_CHAR_PX = 8;
const TEXT_COL_MAX_PX = 480;

function fixedColumnWidthPx(column: TableColumn): number {
  if (column.type === 'duration') {
    return 100;
  }
  if (column.type === 'percent') {
    return 88;
  }
  if (column.type === 'number') {
    return 88;
  }
  return TEXT_COL_MIN_PX;
}

function measureTextColumnWidthPx(
  label: string,
  values: string[],
  compact: boolean,
): number {
  const min = compact ? TEXT_COL_MIN_COMPACT_PX : TEXT_COL_MIN_PX;
  let maxChars = label.length;
  for (const value of values) {
    if (value.length > maxChars) {
      maxChars = value.length;
    }
  }
  return Math.min(
    TEXT_COL_MAX_PX,
    Math.max(min, Math.ceil(maxChars * TEXT_CHAR_PX) + TEXT_COL_PAD_PX),
  );
}

/**
 * Interactive table core: sortable Material columns, search/filter, CSV export.
 * Shared by the compact inline card and the full dialog overlay.
 */
@Component({
  selector: 'app-copilot-table-grid',
  templateUrl: './copilot-table-grid.component.html',
  styleUrls: ['./copilot-table-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopilotTableGridComponent {
  @Input() table: TableSpec | null | undefined;
  /** Cap the number of rendered rows (compact inline view). Omit for no cap. */
  @Input() maxRows: number | null = null;
  /** Show the search box and export button (full view only). */
  @Input() showControls = true;

  searchTerm = '';
  private sortColumn: string | null = null;
  private sortDirection: SortDirection = 'desc';
  private sortInitialized = false;
  private visibleRowsCache: Record<string, unknown>[] = [];
  private visibleRowsCacheKey = '';

  constructor(private readonly cdr: ChangeDetectorRef) {}

  private ensureSortInitialized(): void {
    if (this.sortInitialized) {
      return;
    }
    this.sortInitialized = true;
    const defaultSort: TableSort | undefined = this.table?.defaultSort;
    if (defaultSort && this.table?.columns.some((c) => c.key === defaultSort.column)) {
      this.sortColumn = defaultSort.column;
      this.sortDirection = defaultSort.direction;
    } else {
      const firstSortable = this.table?.columns.find((c) => c.sortable);
      this.sortColumn = firstSortable?.key ?? null;
      this.sortDirection = 'desc';
    }
  }

  get columns(): TableColumn[] {
    return this.table?.columns ?? [];
  }

  get displayedColumns(): string[] {
    return this.columns.map((c) => c.key);
  }

  /** Explicit floor so Material flex cells cannot crush the table under the dialog width. */
  get tableMinWidthPx(): number {
    const cols = this.columns;
    if (!cols.length) {
      return 0;
    }
    return cols.reduce((sum, col) => sum + this.columnWidthPx(col), 32);
  }

  /**
   * Flex mat-table cells size independently — pin every cell in a column to the
   * same px width so long Project/Task labels expand the table instead of wrapping.
   */
  columnWidthPx(column: TableColumn): number {
    if (column.type !== 'text') {
      return fixedColumnWidthPx(column);
    }
    const values = this.allRows.map((row) => this.displayValue(row, column));
    return measureTextColumnWidthPx(column.label, values, !this.showControls);
  }

  columnWidthStyle(column: TableColumn): Record<string, string> {
    const px = `${this.columnWidthPx(column)}px`;
    return {
      flex: `0 0 ${px}`,
      width: px,
      minWidth: px,
      maxWidth: px,
    };
  }

  get activeSortColumn(): string | null {
    this.ensureSortInitialized();
    return this.sortColumn;
  }

  get activeSortDirection(): SortDirection {
    this.ensureSortInitialized();
    return this.sortDirection;
  }

  private get allRows(): Record<string, unknown>[] {
    return this.table?.rows ?? [];
  }

  private get bodySourceRows(): Record<string, unknown>[] {
    return this.allRows.filter((row) => !isSummaryTableRow(row));
  }

  /** Summary / Total rows — always pinned in the footer, never sorted into the body. */
  get footerRows(): Record<string, unknown>[] {
    return this.allRows.filter((row) => isSummaryTableRow(row));
  }

  get hasFooter(): boolean {
    return this.footerRows.length > 0;
  }

  /** First summary row for mat-footer (Material supports one footer row template). */
  get footerRow(): Record<string, unknown> | null {
    return this.footerRows[0] ?? null;
  }

  private get searchedBodyRows(): Record<string, unknown>[] {
    const rows = this.bodySourceRows;
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    const textKeys = this.columns.filter((c) => c.type === 'text').map((c) => c.key);
    return rows.filter((row) =>
      textKeys.some((key) => String(row[key] ?? '').toLowerCase().includes(term)),
    );
  }

  get visibleRows(): Record<string, unknown>[] {
    this.ensureSortInitialized();
    const key = [
      this.table?.id ?? '',
      this.table?.rows?.length ?? 0,
      this.sortColumn ?? '',
      this.sortDirection,
      this.searchTerm,
      this.maxRows ?? '',
    ].join('|');
    if (key === this.visibleRowsCacheKey) {
      return this.visibleRowsCache;
    }
    const rows = [...this.searchedBodyRows];
    const col = this.sortColumn;
    if (col) {
      const dir = this.sortDirection === 'asc' ? 1 : -1;
      rows.sort((a, b) => {
        const av = a[col];
        const bv = b[col];
        if (typeof av === 'number' && typeof bv === 'number') {
          return (av - bv) * dir;
        }
        return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
      });
    }
    const next = this.maxRows != null ? rows.slice(0, this.maxRows) : rows;
    this.visibleRowsCacheKey = key;
    this.visibleRowsCache = next;
    return next;
  }

  get totalRowCount(): number {
    return this.bodySourceRows.length;
  }

  get isTruncated(): boolean {
    return this.maxRows != null && this.searchedBodyRows.length > this.maxRows;
  }

  onSort(sort: Sort): void {
    this.ensureSortInitialized();
    if (!sort.active || !sort.direction) {
      return;
    }
    const col = this.columns.find((c) => c.key === sort.active);
    if (!col?.sortable) {
      return;
    }
    if (this.sortColumn === sort.active && this.sortDirection === sort.direction) {
      return;
    }
    this.sortColumn = sort.active;
    this.sortDirection = sort.direction;
    this.visibleRowsCacheKey = '';
    this.cdr.markForCheck();
  }

  onSearchChange(): void {
    this.visibleRowsCacheKey = '';
    this.cdr.markForCheck();
  }

  /** Gateway TableSpec percent columns are always 0–100. */
  displayValue(row: Record<string, unknown>, column: TableColumn): string {
    const raw = row[column.key];
    if (column.type === 'duration') {
      const formatted = row[`${column.key}Formatted`];
      if (typeof formatted === 'string' && formatted.trim()) {
        return formatted;
      }
      return typeof raw === 'number' ? formatDurationSec(raw) : String(raw ?? '');
    }
    if (column.type === 'percent') {
      return typeof raw === 'number' ? `${raw}%` : String(raw ?? '');
    }
    return String(raw ?? '');
  }

  isMetricColumn(column: TableColumn): boolean {
    return column.type !== 'text';
  }

  columnClass(column: TableColumn): string {
    if (column.type === 'percent') {
      return 'col-percent';
    }
    if (column.type === 'duration') {
      return 'col-duration';
    }
    if (column.type === 'number') {
      return 'col-number';
    }
    return 'col-text';
  }

  exportCsv(): void {
    const t = this.table;
    if (!t) {
      return;
    }
    const header = t.columns.map((c) => csvCell(c.label)).join(',');
    const bodyLines = this.searchedBodyRows.map((row) =>
      t.columns.map((c) => csvCell(this.displayValue(row, c))).join(','),
    );
    const footerLines = this.footerRows.map((row) =>
      t.columns.map((c) => csvCell(this.displayValue(row, c))).join(','),
    );
    const csv = [header, ...bodyLines, ...footerLines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(t.title || 'table').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
